import { ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterContentInit, Component, ComponentRef, ContentChild, ElementRef, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormContainerSize } from '@sb/library/base';
import { Observable, map, startWith, takeUntil } from 'rxjs';
import { FormFieldInputContainerComponent } from '../../../../container/form-field-container.component';
import { FormFieldSelectBaseComponent } from '../../form-field-select-base.component';
import { ChipPreviewComponent } from '../chip-preview/form-field-chip-preview.component';

@Component({
    selector: 'lib-input-chips',
    templateUrl: './form-field-select-input-chips.component.html',
    styleUrls: ['./form-field-select-input-chips.component.scss'],
})

export class FormFieldSelectInputChipsComponent<T extends string> extends FormFieldSelectBaseComponent<string, string[]> implements OnInit, AfterContentInit {
    @Input() showQuickSelect: boolean = true;
    @Input() size: FormContainerSize = FormContainerSize.Medium;

    private _selectedItems: Set<string> = new Set();
    private _remainingItems: Set<string> = new Set();
    private _initialRemainingItems: Set<string> = new Set();
    private _chipsPlacehoderText!: string;
    private _overlayRef!: OverlayRef;
    private _filteredItemsObservable!: Observable<string[]>;
    private _filteredItems!: string[];

    constructor(private _Overlay: Overlay) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        if (!this._selectedItems && this.formControl.value.length === 0 || this._selectedItems && this._selectedItems.size === 0) {
            this._chipsPlacehoderText = this.placeholderText;
        }
        this._selectedItems = new Set(this.formControl.value as string[]);
        this.SortSet(this._selectedItems);
    }

    override ngAfterContentInit(): void {
        super.ngAfterContentInit();
    }

    //We have to override prisitne else where since the view control is just the input text
    override ObservePristine() {
    }

    override SortItems(): void {
        super.SortItems();

        if (!this.nameOf) {
            throw Error(`Could not determine a field to use as a name when setting up (${this.constructor.name}).`)
        }
        this._remainingItems = new Set(this.sortedItemListByGroup.get(null)?.map(item => item.value));
        this._initialRemainingItems = new Set(this._remainingItems);
        this._selectedItems.forEach(item => {
        const itemLowerCase = item.toLocaleLowerCase();
        this._remainingItems.forEach(item => {
            if (item.toLocaleLowerCase() === itemLowerCase) {
                this._remainingItems.delete(item);
            }
        })
        })
        this._filteredItemsObservable = this.viewControl.valueChanges.pipe(
            startWith(''),
            map((value: string) => {
                const items: string[] = (value) ? this.Filter(value) : Array.from(this._remainingItems);
                if (items.length > 200) {  // Limit autocomplete list to avoid performance degradation
                    items.length = 200;
                }

                this._filteredItems = [];

                items.forEach(item => {
                    this._filteredItems.push(item);
                });
                return this._filteredItems;
            })
        );
    }

    override writeValue(values: T[]) {
        this.WriteValue(values);
    }

    Add(event: MatChipInputEvent): void {
        if (!event.value) {
            return;
        }
        this._chipsPlacehoderText = '';
        const selectedItems = new Set(Array.from(this._selectedItems, (item: string) => item.toLocaleLowerCase()));
        if (!selectedItems.has(event.value.toLocaleLowerCase())) {
            this._selectedItems.add(event.value);
        }

        this._remainingItems.forEach(item => {
            if (item.toLocaleLowerCase() === event.value.toLocaleLowerCase()) {
                this._remainingItems.delete(item);
            }
        })
        this.UpdateFormControl();
        this.DetectChanges();
    }

    Remove(item: string) {
        this._selectedItems.delete(item);
        if (this._selectedItems.size === 0) {
            this._chipsPlacehoderText = this.placeholderText;
        }
        this._remainingItems.add(item);
        this.SortSet(this._remainingItems);
        this.UpdateFormControl();
    }

    Selected(item: string) {
        this._remainingItems.delete(item);
        if (this._remainingItems.size) {
            this.autocomplete.openPanel();
        }

        this._selectedItems.add(item);
        this._chipsPlacehoderText = '';
        this.UpdateFormControl();

    }

    Clear() {
        if (!this.autocomplete.panelOpen) {
            this.viewControl.setValue('');
            this.autocomplete.closePanel();
        }
    }

    Blur() {
        this.input?.nativeElement?.blur();
        this.viewControl.setValue('');
        this.autocomplete.closePanel();
    }

    PanelOpened() {
        this.autocomplete.panelClosingActions.pipe(takeUntil(this.autocomplete.autocomplete.closed)).subscribe(_ => {
            this.viewControl.setValue('');
        });
    }

    Clicked(event: MouseEvent) {
        if (!this.isDisabled) {
            event.preventDefault();
            this.input?.nativeElement?.focus();
            this.autocomplete.openPanel();
        }
    }

    ClearAll() {
        this._selectedItems.forEach(item => {
            if (this._initialRemainingItems.has(item)) {
                this._remainingItems.add(item);
                this.SortSet(this._remainingItems);
            }
        })
        this._selectedItems = new Set();

        this.UpdateFormControl();
        this.DetectChanges();
    }

    SelectAll() {
        this._remainingItems.forEach(item => {
            this._selectedItems.add(item);
        })
        this._remainingItems = new Set();
        this.UpdateFormControl();
        this.DetectChanges();
    }

    // Called from search modal to close when event.stopPropagation is used
    CloseIfNotFocused() {
        const activeElement = document.activeElement;
        if ((!activeElement || activeElement !== this.input?.nativeElement) && this.autocomplete.panelOpen) {
            this.autocomplete.closePanel();
        }
    }

    ChipSelected(item: string, event: MouseEvent) {
        if (!this.chipPreviewTemplate) {
            event.stopPropagation();
            return;
        }
        const element = event.target as HTMLElement;
        this._overlayRef = this._Overlay.create(
            {
            maxWidth: '300px',
            maxHeight: '100px',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            positionStrategy: this._Overlay.position().flexibleConnectedTo(element).withPositions(
                [
                    {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 10,
            }, {
                originX: 'start',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom',
                offsetY: -10,
            }])
        });

        const component = new ComponentPortal(ChipPreviewComponent<string>);
        const componentRef: ComponentRef<ChipPreviewComponent<string>> = this._overlayRef.attach(component);
        componentRef.instance.data = item as T;
        componentRef.instance.chipPreviewContentTemplate = this.chipPreviewTemplate;

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef?.dispose();
        });
        event.stopPropagation();
    }

    RemainingItemsHas(remainingItems: Set<string>, item: string): boolean {
        for (const remainItem of remainingItems) {
            if (remainItem.toLocaleLowerCase() === item.toLocaleLowerCase()) {
                return true;
            }
        }
        return false;
    }

    private WriteValue(values: T[], retyring: boolean = false) {
        if (this.sortedItemListByGroup) {
            this._selectedItems = new Set(values as string[]);
            this._remainingItems = new Set();
            this.UpdateFormControl(false);
            this.DetectChanges();
        }
    }

    private UpdateFormControl(markControlDirty: boolean = true): void {
        this.viewControl.setValue('');
        this.formControl.isDirty = markControlDirty;
        this.SetFormControlValue([...this._selectedItems]);

        this.OnBlur();
    }

    private Filter(value: string): string[] {
        const matches: string[] = [];
        value = value.trim().toLocaleLowerCase();
        this._remainingItems.forEach(item => {
            if (item.toLocaleLowerCase().includes(value)) {
                matches.push(item);
            }
        })
        return matches;
    }

    private SortSet(items: Set<string>) {
        const entries = [];
        for (const member of items) {
            entries.push(member);
        }
        items.clear();
        entries.sort((a, b) => a.localeCompare(b, undefined, {'sensitivity': 'base'}));
        for (const entry of entries) {
            items.add(entry);
        }
    };

    get selectedItems(): Set<string> {
        return this._selectedItems;
    }

    get separatorKeyCodes(): number[] {
        return [ENTER, SEMICOLON];
    }

    get chipsPlaceholderText(): string {
        return this.selectedItems.size === 0 ? this._chipsPlacehoderText : "";
    }

    get remainingItems(): Set<string> {
        return this._remainingItems;
    }

    get filteredItems(): Observable<string[]> {
        return this._filteredItemsObservable;
    }

    @ViewChild(MatAutocompleteTrigger, {static: false}) autocomplete!: MatAutocompleteTrigger;
    @ViewChild('input', {static: false}) input?: ElementRef;
    @ContentChild('chipPreview') chipPreviewTemplate!: TemplateRef<any>;
    @ContentChild(TemplateRef, {static: false}) template?: TemplateRef<any>;

}
